import { useLayoutEffect } from 'react'

const revealSelector = [
  '.hero__content > *',
  '.hero-logo-stage',
  '.section-heading',
  '.page-heading',
  '.feature-grid > .panel',
  '.bento > .panel',
  '.rating-flow__stage',
  '.rank-grid > .rank-tier',
  '.rank-tier__emblems > .rank-emblem',
  '.podium > .podium-card',
  '.leaderboard-table tbody > tr',
  '.profile-identity',
  '.stat-grid > .stat-card',
  '.detail-grid > article',
  '.history-panel',
  '.match-list > .match-row',
  '.featured-tournament',
  '.hall-grid > .hall-card',
  '.winner-banner',
  '.bracket > .bracket-round',
  '.participants-grid > .participant-card',
  '.cta-strip > div',
  '.cta-strip > .play-action',
  '.page > .max-w-4xl > .mb-12',
  '.page > .max-w-4xl > .space-y-12 > section',
  '.page > .max-w-4xl > .space-y-8 > section',
].join(', ')

const scaleSelector = '.hero-logo-stage, .rank-emblem, .profile-identity'
const compactSelector =
  '.leaderboard-table tbody > tr, .match-row, .participant-card'

export function ScrollReveal({ routeKey }: { routeKey: string }) {
  useLayoutEffect(() => {
    const mainElement = document.querySelector('main')
    if (!(mainElement instanceof HTMLElement)) return
    const main = mainElement

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const registered = new WeakSet<Element>()
    const revealDelays = new WeakMap<Element, number>()
    const groupCounts = new Map<Element, number>()
    const cleanupTimers = new Set<number>()
    const observer =
      !reducedMotion && 'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return
                entry.target.classList.add('is-revealed')
                observer?.unobserve(entry.target)
                const timer = window.setTimeout(() => {
                  entry.target.classList.remove(
                    'scroll-reveal',
                    'scroll-reveal--compact',
                    'scroll-reveal--scale',
                  )
                  if (entry.target instanceof HTMLElement) {
                    entry.target.style.removeProperty('--reveal-delay')
                  }
                  cleanupTimers.delete(timer)
                }, (revealDelays.get(entry.target) ?? 0) + 850)
                cleanupTimers.add(timer)
              })
            },
            {
              rootMargin: '0px 0px -8% 0px',
              threshold: 0.12,
            },
          )
        : null

    function register(element: HTMLElement) {
      if (registered.has(element)) return
      registered.add(element)

      const group = element.parentElement ?? main
      const order = groupCounts.get(group) ?? 0
      groupCounts.set(group, order + 1)
      const delay = Math.min(order, 5) * 65

      if (observer === null) {
        element.classList.add('is-revealed')
        return
      }

      revealDelays.set(element, delay)
      element.style.setProperty('--reveal-delay', `${delay}ms`)
      element.classList.add('scroll-reveal')
      if (element.matches(scaleSelector)) {
        element.classList.add('scroll-reveal--scale')
      }
      if (element.matches(compactSelector)) {
        element.classList.add('scroll-reveal--compact')
      }

      observer.observe(element)
    }

    function registerWithin(root: ParentNode) {
      if (root instanceof HTMLElement && root.matches(revealSelector)) {
        register(root)
      }
      root
        .querySelectorAll<HTMLElement>(revealSelector)
        .forEach((element) => register(element))
    }

    registerWithin(main)

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) registerWithin(node)
        })
      })
    })
    mutationObserver.observe(main, { childList: true, subtree: true })

    return () => {
      mutationObserver.disconnect()
      observer?.disconnect()
      cleanupTimers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [routeKey])

  return null
}
